import type { FieldTypeKey } from './field-templates.js';

/**
 * F-0779 (`docs/concept-design-agency-production-and-automation.md` §24.6, PX-47, PX-48, PX-54,
 * PX-55): a request checked for clarity at filing. The contract every caller shares — the form, the
 * route, the CLI and MCP: what goes to the AI and nothing else, how its answer is read, the budget of
 * questions the server holds whatever the model says, and the brief a person can send without AI.
 * Generic: nothing here names a package's types, fields or words.
 */

/**
 * The bounds the server holds whatever the model answers (DEC-5, revised by Jay 2026-11-01: "3 is
 * not a hard limit. important is that the important topics are clarified. the assistant can also run
 * in multiple iterations"). The prompt names no count: it asks what matters. These are guards, never
 * a target: at most `CLARITY_MAX_TURNS` rounds, at most `CLARITY_ROUND_QUESTIONS_MAX` questions in a
 * round (what one assistant message can hold as cards) and `CLARITY_MAX_QUESTIONS` in all.
 */
export const CLARITY_MAX_QUESTIONS = 12;
export const CLARITY_MAX_TURNS = 3;
export const CLARITY_ROUND_QUESTIONS_MAX = 6;
/** The message's text the AI reads: its first characters, each inline picture as `[picture]`. */
export const CLARITY_MESSAGE_LIMIT = 4_000;
/** A brief past this, or empty, is no brief. */
export const CLARITY_BRIEF_LIMIT = 6_000;
export const CLARITY_QUESTION_LIMIT = 240;
export const CLARITY_SUGGESTIONS_MAX = 3;
export const CLARITY_SUGGESTION_LIMIT = 60;
/** An answer, and the exchange as a list (every question and later note), past these are refused. */
export const CLARITY_ANSWER_LIMIT = 2_000;
export const CLARITY_EXCHANGE_MAX = 24;
/** The original message, as kept with the item (PX-51). */
export const CLARITY_ORIGINAL_LIMIT = 20_000;
/**
 * How long the server lets the AI take for each step before it answers with the fallback (PX-53).
 * A caller waits for these and a margin for the way, so it never gives up on an answer the server
 * is still allowed to send (F-0785).
 */
export const CLARITY_ASK_TIMEOUT_MS = 15_000;
export const CLARITY_BRIEF_TIMEOUT_MS = 40_000;
export const CLARITY_CALLER_MARGIN_MS = 2_000;

/** How long a caller waits for one step's answer: the server's limit for it and the margin. */
export function clarityCallerWaitMs(step: 'ask' | 'brief'): number {
  return (
    (step === 'brief' ? CLARITY_BRIEF_TIMEOUT_MS : CLARITY_ASK_TIMEOUT_MS) +
    CLARITY_CALLER_MARGIN_MS
  );
}
/** How the item's description was written from the person's message: by the AI, by their edit of it, or plain. */
export const CLARITY_BRIEF_KINDS = ['ai', 'edited', 'plain'] as const;
export type ClarityBriefKindType = (typeof CLARITY_BRIEF_KINDS)[number];

/** What stands in the message for a picture: the AI never reads a picture or its name. */
export const CLARITY_PICTURE = '[picture]';

/**
 * The field types (`FIELD_TYPES`) whose values may go to the AI: text, dates, numbers and choices.
 * Never an email or a link, which name a person or a file; a text field that names a person or an
 * organisation is marked `personal` by the caller and left out too.
 */
export const CLARITY_FIELD_TYPES: ReadonlySet<FieldTypeKey> = new Set<FieldTypeKey>([
  'text',
  'longText',
  'number',
  'date',
  'singleSelect',
  'multiSelect',
]);

/** A question the AI asks, as the person reads it: plain words, up to three answers to tap. */
export interface IClarityQuestion {
  id: string;
  text: string;
  suggestions: string[];
  /**
   * D-0478 a (Jay, 2026-11-01: "use the question engine as in items"): several suggestions may be
   * picked together. Absent, one is: the question turn's `selectionMode` is single unless this is set.
   */
  multiple?: true;
}

/**
 * One step of the exchange: a question asked, with the person's answer (null: skipped), or a later
 * message of theirs (`question: null`, id `note-n`). `turn` says which round asked the question (1 to
 * `CLARITY_MAX_TURNS`).
 */
export interface IClarityExchange {
  id: string;
  question: string | null;
  answer: string | null;
  turn?: number;
}

/** A value the person filled in the form's own fields, as the AI may read it. */
export interface IClarityField {
  label: string;
  value: string;
}

/**
 * Everything a step sends to the AI, and nothing else (PX-55). `round` is the round the ask step
 * would be (1 to `CLARITY_MAX_TURNS`) and `lastRound` whether no round follows it, so the model asks
 * now what it would otherwise leave for later; no count of questions is sent (PX-76).
 */
export interface IClarityInput {
  guide: string;
  message: string;
  fields: IClarityField[];
  exchange: IClarityExchange[];
  language: string;
  round: number;
  lastRound: boolean;
  /** PX-99: the item the step continues, when it continues one; absent at filing. */
  record?: IClarityItemRecord;
}

/**
 * PX-99 (G4, §10.1): who wrote an entry of an item's record, as the AI reads it: a role, never a
 * name. `requester` is whoever asked for the item, `team` a person of the work area, `agent` any
 * agent (one's own harness, ChatGPT or Claude through MCP) and `assistant` the work area's assistant.
 */
export const CLARITY_RECORD_ROLES = ['requester', 'team', 'agent', 'assistant'] as const;
export type ClarityRecordRoleType = (typeof CLARITY_RECORD_ROLES)[number];

/** PX-99: the entries' text the AI reads; past it the oldest are left out, and the block says how many. */
export const CLARITY_RECORD_LIMIT = 8_000;

/**
 * PX-99: one entry of an item's record: a comment, a question with its answer (null: not answered),
 * or a new version of a file, named by which file it is and its kind (`file 2, a PDF`) and its
 * version number, never by the file's name.
 */
export type ClarityRecordEntryType =
  | { kind: 'comment'; by: ClarityRecordRoleType; text: string; at: string }
  | {
      kind: 'question';
      by: ClarityRecordRoleType;
      question: string;
      answer: string | null;
      answeredBy: ClarityRecordRoleType | null;
      at: string;
    }
  | { kind: 'version'; by: ClarityRecordRoleType; slot: string; version: number; at: string };

/**
 * PX-99 (G4): what has already been said on the item a step continues, as labelled data: the
 * description (the brief, when the item was filed through the check) and what its requester first
 * wrote (`original`, null when the item was not filed through the check) always, the plan's values,
 * and the entries oldest first within `CLARITY_RECORD_LIMIT`; `dropped` counts the older entries
 * left out.
 */
export interface IClarityItemRecord {
  description: string;
  original: string | null;
  plan: IClarityField[];
  entries: ClarityRecordEntryType[];
  dropped: number;
}

/**
 * PX-99: an item's record as the server reads it for one reader, before `clarityItemRecordOf` keeps
 * what may go to the AI. The caller maps each author to a role and reads only what the reader may
 * read; nothing that names a person or a file is kept from here, whatever it holds.
 */
export interface IClarityRecordSource {
  description: string;
  original?: string | null;
  plan?: ReadonlyArray<{ label: string; type: string; value: unknown; personal?: boolean }>;
  entries: readonly ClarityRecordEntryType[];
}

/**
 * The ask step's answer, as kept. `clear` only when no question is left. `kind` is the kind of work
 * the model read the request as ("logo", "printed menu card"), when it named one (PX-76): what its
 * questions are about, never shown to the writer as a fact.
 */
export interface IClarityQuestions {
  clear: boolean;
  questions: IClarityQuestion[];
  kind?: string;
}

/** The brief step's answer, as kept: markdown, the person's facts and answers. */
export interface IClarityBrief {
  brief: string;
}

/**
 * The clarity route's one answer shape (`POST …/filing-forms/:itemTypeKey/clarity`), for the form,
 * the CLI, MCP and an agent alike (§24.6): offered or not, who asks, the questions of this turn, or
 * the brief; `fallback` when the step failed or ran out of time.
 */
export interface IClarityAnswer {
  offered: boolean;
  /** PX-62: who asks, in the step's language: the work area's assistant, never a person. */
  assistant: string;
  guide: string | null;
  questions: IClarityQuestion[];
  /** The round the questions belong to (1 to `CLARITY_MAX_TURNS`); null when none were asked. */
  turn: number | null;
  clear: boolean;
  brief: string | null;
  /** The step failed or ran out of time: the message is sent as it was written (PX-53). */
  fallback: boolean;
  /** PX-76: the kind of work the questions were asked for, when the model named one; optional to read. */
  kind?: string;
}

/**
 * One step as a client sends it to the clarity route: `ask` (the questions) or `brief` (the request
 * restated), the message, the form's own field values by field key, and the exchange so far. The
 * route is stateless: each step sends the exchange back.
 */
export interface IClarityStepRequest {
  step: 'ask' | 'brief';
  message: string;
  fields?: Record<string, unknown>;
  exchange?: readonly IClarityExchange[];
  language?: string;
  /** PX-99: the key of the item this step continues; its record is read with the caller's own reading rights. */
  itemKey?: string;
}

/** The questions the exchange holds, skipped ones included. */
function askedOf(exchange: readonly IClarityExchange[]): IClarityExchange[] {
  return exchange.filter((entry) => entry.question !== null);
}

/**
 * How many questions the next round may still ask: what one round holds, within what is left of the
 * whole bound. A guard on what the model answers, never sent to it (PX-47).
 */
export function clarityRemainingOf(exchange: readonly IClarityExchange[]): number {
  return Math.max(
    Math.min(CLARITY_ROUND_QUESTIONS_MAX, CLARITY_MAX_QUESTIONS - askedOf(exchange).length),
    0,
  );
}

/** The round the next ask would be, and whether it may be asked at all (DEC-5, PX-77). */
export function clarityNextTurnOf(exchange: readonly IClarityExchange[]): number | null {
  const asked = askedOf(exchange);
  if (asked.length >= CLARITY_MAX_QUESTIONS) return null;
  const turns = asked.reduce((last, entry) => Math.max(last, entry.turn ?? 1), 0);
  return turns >= CLARITY_MAX_TURNS ? null : turns + 1;
}

/** A question as compared with another: case- and accent-blind, spacing and end marks ignored. */
function questionKeyOf(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[\s\u00a0]+/g, ' ')
    .replace(/[?¿!.\s]+$/u, '')
    .trim();
}

/**
 * Words that carry no topic, in English and German: a question's topic is what is left. Kept short on
 * purpose; a word missing here only makes two questions look a little closer.
 */
const TOPIC_STOPWORDS = new Set(
  (
    'a an the and or but for of to in on at by with from about into as is are was were be been do ' +
    'does did have has had any some your you yours we our us it its this that these those there ' +
    'what which who whom whose how when where why should would could can will must may might want ' +
    'please also just more most other such than then very really specific particular mind ' +
    'der die das den dem des ein eine einen einem einer und oder aber für von zu im in am an auf ' +
    'bei mit aus nach über als ist sind war waren sein hast hat habt haben gibt es du dein deine ' +
    'deinen ihr euer wir uns was welche welcher welches wer wie wann wo warum soll sollen sollte ' +
    'muss müssen kann können willst möchtest noch schon auch nur bitte bestimmte irgendwelche'
  ).split(' '),
);

/** A question's topic: its words without the ones that carry none, accents and plural -s dropped. */
function topicWordsOf(text: string): Set<string> {
  return new Set(
    text
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .split(/[^\p{L}\p{N}]+/u)
      .filter((word) => word.length >= 3 && !TOPIC_STOPWORDS.has(word))
      .map((word) => (word.length > 4 && word.endsWith('s') ? word.slice(0, -1) : word)),
  );
}

/**
 * F-0789 (I-2455 on dev: a skipped "Do you have any symbols, letters, or examples in mind?" came
 * back in the next round as "Do you have any specific symbols (like a crumb or oven), letters, or
 * examples of logos you like or dislike?"): whether two questions ask about the same thing, however
 * reworded. At least two topic words in common, and at least half of the shorter question's.
 */
export function clarityTopicsMatch(one: string, other: string): boolean {
  const a = topicWordsOf(one);
  const b = topicWordsOf(other);
  let shared = 0;
  for (const word of a) if (b.has(word)) shared += 1;
  return shared >= 2 && shared * 2 >= Math.min(a.size, b.size);
}

/**
 * The questions the person skipped and nothing settled since: no later answered question asks the
 * same thing (`clarityTopicsMatch`).
 */
export function claritySkippedOf(exchange: readonly IClarityExchange[]): string[] {
  const answered = exchange.filter((entry) => entry.question !== null && entry.answer !== null);
  return exchange.flatMap((entry, index) =>
    entry.question !== null &&
    entry.answer === null &&
    !answered.some(
      (later) =>
        exchange.indexOf(later) > index && clarityTopicsMatch(entry.question!, later.question!),
    )
      ? [entry.question]
      : [],
  );
}

/** The JSON object in a model's answer: fences and any text around the outer braces dropped. */
function objectOf(raw: string): Record<string, unknown> | null {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  try {
    const value: unknown = JSON.parse(raw.slice(start, end + 1));
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** The months a suggestion may name, by number: English and German, full and short. */
const MONTHS: ReadonlyArray<readonly [RegExp, number]> = [
  [/\b(?:january|jan|januar|jänner)\b/i, 1],
  [/\b(?:february|feb|februar)\b/i, 2],
  [/\b(?:march|mar|märz|maerz|mrz)\b/i, 3],
  [/\b(?:april|apr)\b/i, 4],
  // "may" is also an English verb: only "May" as a name (case kept), or the German "Mai".
  [/\bMay\b|\b[Mm]ai\b/, 5],
  [/\b(?:june|jun|juni)\b/i, 6],
  [/\b(?:july|jul|juli)\b/i, 7],
  [/\b(?:august|aug)\b/i, 8],
  [/\b(?:september|sept|sep)\b/i, 9],
  [/\b(?:october|oct|oktober|okt)\b/i, 10],
  [/\b(?:november|nov)\b/i, 11],
  [/\b(?:december|dec|dezember|dez)\b/i, 12],
];

/** The facts a text states: each number in it (leading zeros dropped), and each month it names. */
function factsOf(text: string): { numbers: Set<string>; months: Set<number> } {
  const numbers = new Set((text.match(/\d+/g) ?? []).map((run) => run.replace(/^0+(?=\d)/, '')));
  const months = new Set<number>();
  for (const [pattern, month] of MONTHS) if (pattern.test(text)) months.add(month);
  return { numbers, months };
}

/**
 * PX-68: whether a suggested answer states a fact the writer never gave: a number (a price, an
 * amount, a day, a year) or a month that none of `sources` (the message, the fields' values, the
 * guide and the answers so far) holds. "Valid until May 31st" is such a suggestion when nobody
 * wrote May or 31; "A fixed price" and "I'll tell you" are not. A backstop to the ask prompt's own
 * rule: the model's word is never taken for a fact.
 */
export function claritySuggestionInvents(suggestion: string, sources: readonly string[]): boolean {
  const said = factsOf(sources.join('\n'));
  const offered = factsOf(suggestion);
  for (const number of offered.numbers) if (!said.numbers.has(number)) return true;
  for (const month of offered.months) if (!said.months.has(month)) return true;
  return false;
}

/** PX-68: what the writer and the guide said, in the input one step sends: the facts a suggestion may repeat. */
export function claritySourcesOf(input: IClarityInput): string[] {
  return [
    input.guide,
    input.message,
    ...input.fields.map((field) => field.value),
    ...input.exchange.flatMap((entry) => (entry.answer === null ? [] : [entry.answer])),
    ...clarityRecordSourcesOf(input.record),
  ];
}

/**
 * F-0789 (Jay, I-2455: an unanswered question already counts as skipped): a suggestion that only
 * puts the answer off ("I will tell you", "Later", "Not sure", „Sag ich dir später“) is no answer to
 * tap, and is dropped whatever the prompt said.
 */
const FILLER_SUGGESTIONS: readonly RegExp[] = [
  /^i(?:'|’| wi)ll (?:tell|let) you(?: know)?(?: later)?$/i,
  /^i(?:'|’| wi)ll (?:give|send|provide|share|specify|confirm|decide)\b.*\blater$/i,
  /^(?:(?:i'?ll )?tell (?:you )?later|later|not sure(?: yet)?|unsure|no idea|undecided|(?:i )?don'?t know(?: yet)?|to be decided|tbd)$/i,
  /^(?:ich )?sag(?:e)? (?:ich )?(?:es )?(?:dir|euch) (?:noch|später|bescheid)\b.*$/i,
  /^(?:später|noch offen|noch unklar|keine ahnung|(?:ich )?weiß (?:ich )?(?:es )?(?:noch )?nicht)$/i,
];

export function claritySuggestionIsFiller(suggestion: string): boolean {
  const said = suggestion.trim().replace(/[.!…]+$/u, '');
  return FILLER_SUGGESTIONS.some((pattern) => pattern.test(said));
}

/** One question, as kept: a single question of 1 to 240 characters, its suggestions trimmed. */
function questionOf(value: unknown, sources?: readonly string[]): IClarityQuestion | null {
  if (value === null || typeof value !== 'object') return null;
  const { id, text, suggestions, multiple } = value as Record<string, unknown>;
  if (typeof id !== 'string' || id.trim() === '' || typeof text !== 'string') return null;
  const question = text.trim();
  if (question.length < 1 || question.length > CLARITY_QUESTION_LIMIT) return null;
  // One question: it ends with its only question mark.
  if (!question.endsWith('?') || question.indexOf('?') !== question.length - 1) return null;
  const offered = Array.isArray(suggestions) ? suggestions : [];
  const kept = offered
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0 && entry.length <= CLARITY_SUGGESTION_LIMIT)
    // F-0789: a filler that only puts the answer off is no choice; skipping says it.
    .filter((entry) => !claritySuggestionIsFiller(entry))
    // PX-68: a suggestion that states a fact the writer never gave is dropped, not shortened.
    .filter((entry) => !sources || !claritySuggestionInvents(entry, sources))
    .slice(0, CLARITY_SUGGESTIONS_MAX);
  // F-0789: a single option left is no choice: the question is answered in the writer's own words.
  const choices = kept.length >= 2 ? kept : [];
  return {
    id: id.trim(),
    text: question,
    suggestions: choices,
    // D-0478 a: single unless the model says several answers hold together; free text has no picks.
    ...(multiple === true && choices.length > 0 ? { multiple: true as const } : {}),
  };
}

/** D-0478 a: an answer's parts, the empty left out, joined with "and" in `language`. */
export function clarityPicksJoined(parts: readonly string[], language: string): string {
  return new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(
    parts.map((part) => part.trim()).filter((part) => part !== ''),
  );
}

/**
 * The ask step's answer as kept, or null when nothing in it can be kept (the step's failure, never
 * repaired by a second call). A question is kept when it is one question and it repeats none asked
 * before or kept just now; an id used before is replaced by a new one; past the bounds, questions
 * are dropped. `clear: true`
 * with questions reads as questions. Given `sources` (PX-68: what the writer and the guide said), a
 * suggestion stating a number or month none of them holds is dropped; the question stays, answered
 * in the writer's own words.
 */
export function clarityQuestionsOf(
  raw: string,
  exchange: readonly IClarityExchange[],
  sources?: readonly string[],
): IClarityQuestions | null {
  const answer = objectOf(raw);
  if (!answer) return null;
  const listed = Array.isArray(answer.questions) ? answer.questions : [];
  const remaining = clarityNextTurnOf(exchange) === null ? 0 : clarityRemainingOf(exchange);
  const kind =
    typeof answer.kind === 'string' && answer.kind.trim() !== ''
      ? answer.kind.trim().slice(0, CLARITY_SUGGESTION_LIMIT)
      : undefined;
  const ids = new Set(exchange.map((entry) => entry.id));
  const seen = new Set(askedOf(exchange).map((entry) => questionKeyOf(entry.question ?? '')));
  // F-0789: a skipped question is never asked again, in other words either.
  const skipped = exchange.flatMap((entry) =>
    entry.question !== null && entry.answer === null ? [entry.question] : [],
  );
  const questions: IClarityQuestion[] = [];
  let repeated = 0;
  for (const value of listed) {
    if (questions.length >= remaining) break;
    const question = questionOf(value, sources);
    if (!question) continue;
    const key = questionKeyOf(question.text);
    if (seen.has(key) || skipped.some((asked) => clarityTopicsMatch(asked, question.text))) {
      repeated += 1;
      continue;
    }
    // A later round's answer numbers its questions from q1 again: a reused id is a new question.
    for (let next = 2; ids.has(question.id); next += 1) {
      question.id = `${question.id.replace(/-\d+$/, '')}-${next}`;
    }
    ids.add(question.id);
    seen.add(key);
    questions.push(question);
  }
  if (questions.length > 0) return { clear: false, questions, ...(kind ? { kind } : {}) };
  // Nothing to ask: clear when the model said so, or when all it asked was asked before (PX-77: a
  // later round with nothing new ends the rounds); else an answer with nothing kept.
  return answer.clear === true || repeated > 0
    ? { clear: true, questions: [], ...(kind ? { kind } : {}) }
    : null;
}

/**
 * F-0789 (AR-0007 on dev: "What is missing? Promotion terms: The current exchange asks for exact
 * terms … but the answers provided were …"): words that only commentary on the check uses, never a
 * request a producer reads. English and German.
 */
const BRIEF_COMMENTARY =
  /\b(?:the (?:current )?exchange|answers? (?:provided|given)|(?:the|this) assistant|(?:was|were) (?:not )?asked|questions? asked|der austausch|im austausch|die gegebenen antworten|(?:der|die) assistent(?:in)?|wurde(?:n)? (?:nicht )?gefragt)\b/i;

/** Whether a brief comments on the check instead of stating the request (F-0789). */
export function clarityBriefComments(brief: string): boolean {
  return BRIEF_COMMENTARY.test(brief);
}

/**
 * The brief step's answer as kept: non-empty markdown within its length that states the request,
 * or null. A brief that comments on the check (F-0789) is no brief: the caller files the plain one.
 */
export function clarityBriefOf(raw: string): IClarityBrief | null {
  const answer = objectOf(raw);
  const brief = typeof answer?.brief === 'string' ? answer.brief.trim() : '';
  if (brief.length === 0 || brief.length > CLARITY_BRIEF_LIMIT) return null;
  if (clarityBriefComments(brief)) return null;
  return { brief };
}

/** The "Still open" heading in each language the brief is written in; English for any other. */
export const CLARITY_STILL_OPEN: Readonly<Record<'en' | 'de', string>> = {
  en: 'Still open',
  de: 'Noch offen',
};

/** A line that heads the "Still open" section, as a heading, a bold line or a label. */
const STILL_OPEN_HEADING =
  /^\s*(?:#{1,6}\s*)?(?:\*\*|__)?\s*(?:still open|noch offen)\s*:?\s*(?:\*\*|__)?\s*:?\s*$/i;
/** A line that starts the next section. */
const BRIEF_HEADING = /^\s*(?:#{1,6}\s+\S|(?:\*\*|__)[^*_]+(?:\*\*|__)\s*:?\s*$)/;

/**
 * F-0789 (I-2455 on dev: a question skipped in round 2 missing from the brief): every question the
 * person skipped, and nothing settled since (`claritySkippedOf`), is listed under "Still open",
 * whatever the model wrote. One the model's section already names (`clarityTopicsMatch`) is not
 * listed twice; with no such section, one is added at the end, its heading in `language`.
 */
export function clarityBriefStillOpenOf(
  brief: string,
  exchange: readonly IClarityExchange[],
  language: string,
): string {
  const skipped = claritySkippedOf(exchange);
  if (skipped.length === 0) return brief;
  const lines = brief.split('\n');
  const start = lines.findIndex((line) => STILL_OPEN_HEADING.test(line));
  if (start < 0) {
    const heading = CLARITY_STILL_OPEN[language === 'de' ? 'de' : 'en'];
    return `${brief.trimEnd()}\n\n## ${heading}\n\n${skipped.map((text) => `- ${text}`).join('\n')}`;
  }
  let end = lines.findIndex((line, index) => index > start && BRIEF_HEADING.test(line));
  if (end < 0) end = lines.length;
  const section = lines.slice(start + 1, end);
  const missing = skipped.filter(
    (text) => !section.some((line) => line.trim() !== '' && clarityTopicsMatch(text, line)),
  );
  if (missing.length === 0) return brief;
  // After the section's last line with words, so the list stays one list.
  let last = end - 1;
  while (last > start && lines[last].trim() === '') last -= 1;
  return [
    ...lines.slice(0, last + 1),
    ...missing.map((text) => `- ${text}`),
    ...lines.slice(last + 1),
  ].join('\n');
}

/** PX-54: a link written in the brief, found by `clarityBriefLinksOf`. */
const BRIEF_LINK =
  /`[^`\n]*`|(!?)\[([^\]\n]*)\]\(([^)\s]+)(?:\s+"[^"\n]*")?\)|<(https?:\/\/[^>\s]+)>|(https?:\/\/[^\s<>()[\]`]+)/gi;

/**
 * PX-54 (`docs/concept-design-agency-production-and-automation.md` §24.6): a link in the brief that is
 * not in the writer's message or answers is drawn as text, so the AI can never put a link of its own
 * in front of a reader. A markdown link or picture becomes its words followed by its address in code,
 * an autolink or a bare address becomes the address in code; one found in `sources` (the message and
 * the answers, as written) is kept as it is. Code spans are left alone.
 */
export function clarityBriefLinksOf(brief: string, sources: readonly string[]): string {
  const known = (address: string) => sources.some((source) => source.includes(address));
  const code = (address: string) => `\`${address.replace(/`/g, '')}\``;
  return brief.replace(
    BRIEF_LINK,
    (whole, _picture: string | undefined, label: string | undefined, target, auto, bare) => {
      if (whole.startsWith('`')) return whole;
      if (target !== undefined) {
        if (known(target)) return whole;
        const words = (label ?? '').trim();
        return words ? `${words} (${code(target)})` : code(target);
      }
      if (auto !== undefined) return known(auto) ? whole : code(auto);
      // A bare address ends before the sentence's own punctuation.
      const address = (bare as string).replace(/[.,;:!?'"]+$/, '');
      const rest = (bare as string).slice(address.length);
      return known(address) ? whole : `${code(address)}${rest}`;
    },
  );
}

/** A message's text as the AI reads it: each inline picture as `[picture]`, its first 4,000 characters. */
export function clarityMessageOf(message: string): string {
  return message
    .replace(/!\[[^\]]*\]\([^)]*\)/g, () => CLARITY_PICTURE)
    .replace(/<img\b[^>]*>/gi, () => CLARITY_PICTURE)
    .slice(0, CLARITY_MESSAGE_LIMIT);
}

/**
 * The input of one step, holding only what may go to the AI (PX-55): the guide, the message's text,
 * the values of the form's own fields of the kinds that name no person, organisation or file, the
 * exchange, the language and which round this is. Nothing else is read from the arguments.
 */
export function clarityInputOf(source: {
  guide: string;
  message: string;
  fields?: ReadonlyArray<{ label: string; type: string; value: unknown; personal?: boolean }>;
  exchange?: readonly IClarityExchange[];
  language: string;
  record?: IClarityRecordSource;
}): IClarityInput {
  const exchange = (source.exchange ?? []).map(({ id, question, answer, turn }) => ({
    id,
    question,
    answer,
    ...(turn !== undefined ? { turn } : {}),
  }));
  // The round an ask would be; a brief step reads the same input, past the last round.
  const round = clarityNextTurnOf(exchange) ?? CLARITY_MAX_TURNS;
  return {
    guide: source.guide,
    message: clarityMessageOf(source.message),
    fields: clarityFieldsOf(source.fields),
    exchange,
    language: source.language,
    round,
    lastRound: round >= CLARITY_MAX_TURNS,
    ...(source.record ? { record: clarityItemRecordOf(source.record) } : {}),
  };
}

/** The values of fields of the kinds that name no person, organisation or file (PX-55). */
function clarityFieldsOf(
  source: ReadonlyArray<{ label: string; type: string; value: unknown; personal?: boolean }> = [],
): IClarityField[] {
  return source.flatMap(({ label, type, value, personal }) => {
    if (personal || !CLARITY_FIELD_TYPES.has(type as FieldTypeKey)) return [];
    const text = Array.isArray(value)
      ? value.filter((entry) => typeof entry === 'string' || typeof entry === 'number').join(', ')
      : typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : '';
    return text.trim() === '' ? [] : [{ label, value: text.trim() }];
  });
}

/** Which file a version is of, as the AI reads it (`file 2, a PDF`), never the file's name. */
const CLARITY_SLOT_LIMIT = 80;

/** PX-99: a text of the record as the AI reads it: pictures as `[picture]`, within `limit`. */
function recordTextOf(text: string, limit: number = CLARITY_ANSWER_LIMIT): string {
  return text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, () => CLARITY_PICTURE)
    .replace(/<img\b[^>]*>/gi, () => CLARITY_PICTURE)
    .slice(0, limit);
}

function isRecordRole(role: unknown): role is ClarityRecordRoleType {
  return (CLARITY_RECORD_ROLES as readonly unknown[]).includes(role);
}

/** PX-99: one entry as it may go to the AI, or null when its author has no role. */
function recordEntryOf(entry: ClarityRecordEntryType): ClarityRecordEntryType | null {
  if (!isRecordRole(entry.by)) return null;
  if (entry.kind === 'comment') {
    return { kind: 'comment', by: entry.by, text: recordTextOf(entry.text), at: entry.at };
  }
  if (entry.kind === 'question') {
    return {
      kind: 'question',
      by: entry.by,
      question: recordTextOf(entry.question),
      answer: entry.answer === null ? null : recordTextOf(entry.answer),
      answeredBy: isRecordRole(entry.answeredBy) ? entry.answeredBy : null,
      at: entry.at,
    };
  }
  return {
    kind: 'version',
    by: entry.by,
    slot: entry.slot.slice(0, CLARITY_SLOT_LIMIT),
    version: entry.version,
    at: entry.at,
  };
}

/** PX-99: the characters an entry spends of `CLARITY_RECORD_LIMIT`. */
function recordLengthOf(entry: ClarityRecordEntryType): number {
  if (entry.kind === 'comment') return entry.text.length;
  if (entry.kind === 'question') return entry.question.length + (entry.answer ?? '').length;
  return entry.slot.length;
}

/**
 * PX-99 (G4, §10.1): what of an item's record may go to the AI, as labelled data. The description
 * (within a brief's length) and what the requester first wrote (as a message is read) are always kept; the plan's values as a form's fields
 * are; each entry is rebuilt from its kind's own keys, so a name, an address or a file's name a source
 * carries never reaches the AI, and an entry whose author has no role is left out. Past
 * `CLARITY_RECORD_LIMIT` characters of entries the oldest are left out, and `dropped` says how many.
 */
export function clarityItemRecordOf(source: IClarityRecordSource): IClarityItemRecord {
  const entries = source.entries.flatMap((entry) => recordEntryOf(entry) ?? []);
  let spent = 0;
  let from = entries.length;
  while (from > 0 && spent + recordLengthOf(entries[from - 1]) <= CLARITY_RECORD_LIMIT) {
    from -= 1;
    spent += recordLengthOf(entries[from]);
  }
  return {
    description: recordTextOf(source.description, CLARITY_BRIEF_LIMIT),
    original: source.original?.trim() ? clarityMessageOf(source.original) : null,
    plan: clarityFieldsOf(source.plan),
    entries: entries.slice(from),
    dropped: from,
  };
}

/** PX-68, PX-99: what the item's record says, as facts a suggestion may repeat. */
export function clarityRecordSourcesOf(record: IClarityItemRecord | undefined): string[] {
  if (!record) return [];
  return [
    record.description,
    ...(record.original === null ? [] : [record.original]),
    ...record.plan.map((field) => field.value),
    ...record.entries.flatMap((entry) => {
      if (entry.kind === 'comment') return [entry.text];
      if (entry.kind === 'question') return entry.answer === null ? [] : [entry.answer];
      return [];
    }),
  ];
}

/**
 * Send as is (PX-48), with no AI: the message as it was written, then, when any question
 * was answered, the answers as a plain list under `words.yourAnswers`. A skipped question is left out;
 * a later message is listed as it was written.
 */
export function plainBriefOf(
  message: string,
  exchange: readonly IClarityExchange[],
  words: { yourAnswers: string },
): string {
  const answered = exchange.filter((entry) => entry.answer !== null && entry.answer.trim() !== '');
  if (answered.length === 0) return message;
  const lines = answered.map((entry) =>
    entry.question === null
      ? `- ${entry.answer!.trim()}`
      : `- ${entry.question.trim()} ${entry.answer!.trim()}`,
  );
  return `${message.trimEnd()}\n\n**${words.yourAnswers}**\n\n${lines.join('\n')}`;
}

/**
 * PX-51: what a filing keeps with the item, once, on its `created` event: the original message, the
 * questions and the answers to them, and how the description (the brief) was written.
 */
export interface IClarityFiling {
  original: string;
  exchange: IClarityExchange[];
  brief: ClarityBriefKindType;
}

/**
 * A filing's `clarity` read strictly, as every create takes it: the original as text within its
 * length, the exchange within the bounds (`CLARITY_MAX_QUESTIONS` over `CLARITY_MAX_TURNS` rounds,
 * each within its length),
 * the brief's kind. Anything else in it is dropped. Absent, nothing is kept; malformed, the problem
 * to refuse the filing with.
 */
export function clarityFilingOf(
  raw: unknown,
): { filing: IClarityFiling | null; problem: null } | { filing: null; problem: string } {
  if (raw === undefined || raw === null) return { filing: null, problem: null };
  const refused = (problem: string) => ({ filing: null, problem });
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return refused('"clarity" is { original, exchange, brief }.');
  }
  const { original, exchange, brief } = raw as Record<string, unknown>;
  if (typeof original !== 'string' || original.trim() === '') {
    return refused('"clarity.original" is the message as it was written.');
  }
  if (original.length > CLARITY_ORIGINAL_LIMIT) {
    return refused(`"clarity.original" is at most ${CLARITY_ORIGINAL_LIMIT} characters.`);
  }
  if (!CLARITY_BRIEF_KINDS.includes(brief as ClarityBriefKindType)) {
    return refused(`"clarity.brief" is one of ${CLARITY_BRIEF_KINDS.join(', ')}.`);
  }
  const listed = exchange === undefined || exchange === null ? [] : exchange;
  if (!Array.isArray(listed) || listed.length > CLARITY_EXCHANGE_MAX) {
    return refused(`"clarity.exchange" is a list of at most ${CLARITY_EXCHANGE_MAX} entries.`);
  }
  const kept: IClarityExchange[] = [];
  const ids = new Set<string>();
  for (const entry of listed) {
    const { id, question, answer, turn } = (entry ?? {}) as Record<string, unknown>;
    const within = (part: unknown, limit: number) =>
      part === null || (typeof part === 'string' && part.length <= limit);
    if (
      typeof id !== 'string' ||
      id === '' ||
      ids.has(id) ||
      !within(question, CLARITY_QUESTION_LIMIT) ||
      !within(answer, CLARITY_ANSWER_LIMIT) ||
      (turn !== undefined && !isClarityTurn(turn))
    ) {
      return refused(
        `"clarity.exchange" entries are { id, question, answer, turn? }: a new id, texts within their lengths or null, a turn of 1 to ${CLARITY_MAX_TURNS}.`,
      );
    }
    ids.add(id);
    kept.push({
      id,
      question: question as string | null,
      answer: answer as string | null,
      ...(isClarityTurn(turn) ? { turn } : {}),
    });
  }
  if (askedOf(kept).length > CLARITY_MAX_QUESTIONS) {
    return refused(`"clarity.exchange" holds at most ${CLARITY_MAX_QUESTIONS} questions.`);
  }
  return {
    filing: { original, exchange: kept, brief: brief as ClarityBriefKindType },
    problem: null,
  };
}

/** A round's number as a filing may carry it: a whole number from 1 to `CLARITY_MAX_TURNS`. */
function isClarityTurn(turn: unknown): turn is number {
  return Number.isInteger(turn) && (turn as number) >= 1 && (turn as number) <= CLARITY_MAX_TURNS;
}

/**
 * PX-56: the `clarity` a terminal or an agent files beside its brief (`featit new --original-file`,
 * MCP `create`'s `original`), before `clarityFilingOf` reads it on the server. Neither can say
 * whether a person edited the brief the check wrote, so the brief's kind follows what was sent: an
 * exchange (even an empty one: the check found the request clear) says the brief came from the
 * check, `ai`; the original alone says the description was written without it, `plain`. `edited` is
 * the form's alone, where the edit is seen.
 */
export function clarityFilingSent(
  original: string,
  exchange: unknown,
): { original: string; exchange: unknown; brief: ClarityBriefKindType } {
  return exchange === undefined || exchange === null
    ? { original, exchange: [], brief: 'plain' }
    : { original, exchange, brief: 'ai' };
}

/** PX-62: the assistant's own name, as its owner may set it. */
export const CLARITY_ASSISTANT_NAME_LIMIT = 60;
/** PX-62 (D-0472 b): the assistant's name while its owner sets none, per language (German a draft). */
export const CLARITY_ASSISTANT_DEFAULT_NAME: Readonly<Record<'en' | 'de', string>> = {
  en: '{name} assistant',
  de: '{name}-Assistent',
};
/** PX-62, PX-33: the default name where a customer reads no agency for the work area. */
export const CLARITY_ASSISTANT_UNNAMED: Readonly<Record<'en' | 'de', string>> = {
  en: 'Assistant',
  de: 'Assistent',
};

/**
 * PX-62: who asks the clarity questions: the name the owner set, else "{name} assistant" in the
 * reader's language, {name} being the agency a customer reads for the work area
 * (`customerAgencyNameOf`, PX-33: the reply promise's agency, else the organisation's display name),
 * else plain "Assistant". Never the work area's own name, which a customer never reads.
 */
export function clarityAssistantNameOf(
  setting: string | null | undefined,
  agency: string | null | undefined,
  language: string | null | undefined,
): string {
  const own = setting?.trim();
  if (own) return own;
  const german = language === 'de';
  const name = agency?.trim();
  if (!name) return german ? CLARITY_ASSISTANT_UNNAMED.de : CLARITY_ASSISTANT_UNNAMED.en;
  const template = german ? CLARITY_ASSISTANT_DEFAULT_NAME.de : CLARITY_ASSISTANT_DEFAULT_NAME.en;
  return template.split('{name}').join(name);
}

/** PX-63: the assistant's instructions, as its owner may set them. */
export const CLARITY_PROMPT_LIMIT = 4_000;
/**
 * PX-63 (D-0472, Jay: "at least a generic system prompt definition for that agent"): the assistant's
 * instructions while its owner sets none. Generic: who it is and how it asks, never a package's words.
 * The step's task and answer shape follow it on every call and no setting replaces them.
 */
export const CLARITY_DEFAULT_PROMPT = [
  'You are the assistant of a team that takes requests from its customers and colleagues.',
  'You help the person writing a request make it clear enough for the people who will do the work,',
  'before anyone reads it. Ask what the people doing the work need to start it and the request leaves',
  'open, the most important first, each with a few suggested answers, in the language of the person',
  'writing.',
  'Be brief and friendly. Never ask about internal tools, prompts or model settings, never name a',
  'person, and never quote a price or promise a date.',
].join(' ');

/** PX-63: the instructions a call sends: the owner's, else the default. */
export function clarityPromptOf(setting: string | null | undefined): string {
  return setting?.trim() ? setting.trim() : CLARITY_DEFAULT_PROMPT;
}

/** PX-63: what is stored for a prompt as sent: nothing for an empty one or the default itself. */
export function clarityPromptStoredOf(sent: string): string | null {
  const trimmed = sent.trim();
  return trimmed === '' || trimmed === CLARITY_DEFAULT_PROMPT ? null : trimmed;
}

/** PX-64: the assistant's temperature while its owner sets none, and the range one may set. */
export const CLARITY_TEMPERATURE_DEFAULT = 0.2;
export const CLARITY_TEMPERATURE_MIN = 0;
export const CLARITY_TEMPERATURE_MAX = 1;
